import React, { useEffect, useState } from 'react';
import {
  Table, Tag, Typography, Input, Select, Space, Card, Button,
  Drawer, Descriptions, Badge, Tooltip, message, Dropdown
} from 'antd';
import {
  ReloadOutlined, EyeOutlined, StopOutlined,
  GlobalOutlined, DesktopOutlined, MobileOutlined, DownOutlined
} from '@ant-design/icons';
import { getLogs, addBlacklist } from '../services/api';

const { Text, Paragraph } = Typography;
const { Search } = Input;

function LogsPage() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [ipFilter, setIpFilter] = useState('');
  const [blockedFilter, setBlockedFilter] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    loadData();
  }, [page, pageSize]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (ipFilter) params.ip = ipFilter;
      if (blockedFilter !== null) params.is_blocked = blockedFilter;
      const res = await getLogs(params);
      setData(res.data.items);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => { setPage(1); loadData(); };

  const showDetail = (record) => {
    setSelectedLog(record);
    setDrawerVisible(true);
  };

  const quickBlock = async (type, value) => {
    if (!value) { message.warning('值为空，无法拉黑'); return; }
    try {
      await addBlacklist({ type, value, reason: '从日志快速拉黑' });
      message.success(`已拉黑 ${type}: ${value}`);
      loadData();
    } catch (err) {
      message.error(err.response?.data?.error || '操作失败');
    }
  };

  // Build block menu items for a record
  const getBlockMenuItems = (record) => {
    const fpData = record.fingerprint_data
      ? (typeof record.fingerprint_data === 'string' ? JSON.parse(record.fingerprint_data) : record.fingerprint_data)
      : null;
    const items = [];
    if (record.ip) {
      items.push({ key: 'ip', label: `拉黑 IP: ${record.ip}`, onClick: () => quickBlock('ip', record.ip) });
    }
    if (record.real_ip && record.real_ip !== record.ip) {
      items.push({ key: 'real_ip', label: `拉黑真实IP: ${record.real_ip}`, onClick: () => quickBlock('ip', record.real_ip) });
    }
    if (record.webrtc_ips) {
      record.webrtc_ips.split(',').forEach((ip, i) => {
        if (ip && ip !== record.ip) {
          items.push({ key: `webrtc_${i}`, label: `拉黑 WebRTC IP: ${ip}`, onClick: () => quickBlock('webrtc_ip', ip) });
        }
      });
    }
    if (record.asn) {
      items.push({ key: 'asn', label: `拉黑 ASN: ${record.asn}`, onClick: () => quickBlock('asn', record.asn) });
    }
    if (record.fingerprint) {
      items.push({ key: 'fp', label: `拉黑指纹: ${record.fingerprint.slice(0, 12)}...`, onClick: () => quickBlock('fingerprint', record.fingerprint) });
    }
    if (fpData && fpData.c && fpData.c !== 'e') {
      items.push({ key: 'canvas', label: `拉黑 Canvas: ${fpData.c.slice(0, 12)}...`, onClick: () => quickBlock('canvas', fpData.c) });
    }
    const ua = fpData?.ua || record.user_agent;
    if (ua) {
      items.push({ key: 'ua', label: `拉黑 UA: ${ua.slice(0, 30)}...`, onClick: () => quickBlock('user_agent', ua) });
    }
    return items;
  };

  const columns = [
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
      width: 125,
      render: (val) => <Text code style={{ fontSize: 11 }}>{val}</Text>
    },
    {
      title: '真实IP',
      dataIndex: 'real_ip',
      key: 'real_ip',
      width: 125,
      render: (val) => val ? <Text code style={{ fontSize: 11, color: '#fa541c' }}>{val}</Text> : '-'
    },
    {
      title: 'ASN',
      dataIndex: 'asn',
      key: 'asn',
      width: 70,
      render: (val) => val ? <Tag color="geekblue">{val}</Tag> : '-'
    },
    {
      title: '指纹',
      dataIndex: 'fingerprint',
      key: 'fingerprint',
      width: 95,
      ellipsis: true,
      render: (val) => val ? (
        <Tooltip title={val}>
          <Text code style={{ fontSize: 10 }}>{val?.slice(0, 10)}...</Text>
        </Tooltip>
      ) : '-'
    },
    {
      title: '文件',
      dataIndex: 'file_id',
      key: 'file_id',
      width: 80,
      render: (val) => val ? <Tag>{val}</Tag> : '-'
    },
    {
      title: '评分',
      dataIndex: 'risk_score',
      key: 'risk_score',
      width: 55,
      render: (val) => {
        if (val === null || val === undefined) return <Text type="secondary" style={{ fontSize: 10 }}>-</Text>;
        const s = parseFloat(val);
        const color = s >= 8 ? '#52c41a' : s >= 5 ? '#faad14' : s >= 3 ? '#ff7a45' : '#ff4d4f';
        return <Tag color={color} style={{ fontSize: 11, fontWeight: 600 }}>{s}</Tag>;
      }
    },
    {
      title: '状态',
      dataIndex: 'is_blocked',
      key: 'is_blocked',
      width: 70,
      render: (val) => val
        ? <Badge status="error" text={<Text type="danger" style={{ fontSize: 11 }}>拦截</Text>} />
        : <Badge status="success" text={<Text type="success" style={{ fontSize: 11 }}>放行</Text>} />
    },
    {
      title: '时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 135,
      render: (val) => <Text type="secondary" style={{ fontSize: 11 }}>{val}</Text>
    },
    {
      title: '操作',
      key: 'action',
      width: 110,
      render: (_, record) => (
        <Space size={2}>
          <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => showDetail(record)} />
          <Dropdown menu={{ items: getBlockMenuItems(record) }} trigger={['click']}>
            <Button type="text" size="small" danger icon={<StopOutlined />}>
              <DownOutlined style={{ fontSize: 10 }} />
            </Button>
          </Dropdown>
        </Space>
      )
    },
  ];

  // Parse fingerprint data for detail view
  const fpData = selectedLog?.fingerprint_data
    ? (typeof selectedLog.fingerprint_data === 'string'
        ? JSON.parse(selectedLog.fingerprint_data)
        : selectedLog.fingerprint_data)
    : null;

  return (
    <div>
      <Card size="small" bordered={false} style={{ marginBottom: 12, borderRadius: 8 }}>
        <Space wrap>
          <Search
            placeholder="搜索 IP"
            value={ipFilter}
            onChange={(e) => setIpFilter(e.target.value)}
            onSearch={handleSearch}
            style={{ width: 180 }}
            allowClear
            size="small"
          />
          <Select
            allowClear
            placeholder="状态"
            style={{ width: 100 }}
            size="small"
            value={blockedFilter}
            onChange={(val) => setBlockedFilter(val)}
          >
            <Select.Option value="0">放行</Select.Option>
            <Select.Option value="1">拦截</Select.Option>
          </Select>
          <Button size="small" icon={<ReloadOutlined />} onClick={handleSearch}>
            刷新
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            共 {total} 条
          </Text>
        </Space>
      </Card>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        size="small"
        scroll={{ x: 900 }}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          size: 'small',
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); }
        }}
      />

      {/* 详情抽屉 */}
      <Drawer
        title={
          <Space>
            <DesktopOutlined />
            <span>访问详情 #{selectedLog?.id}</span>
            {selectedLog?.is_blocked
              ? <Badge status="error" text="已拦截" />
              : <Badge status="success" text="已放行" />
            }
          </Space>
        }
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={560}
      >
        {selectedLog && (
          <div>
            {/* 网络信息 */}
            <Descriptions
              title={<><GlobalOutlined /> 网络信息</>}
              size="small"
              column={2}
              bordered
              style={{ marginBottom: 20 }}
            >
              <Descriptions.Item label="CF IP">{selectedLog.ip || '-'}</Descriptions.Item>
              <Descriptions.Item label="真实IP">
                <Space>
                  <Text code style={{ color: '#fa541c' }}>{selectedLog.real_ip || '-'}</Text>
                  {selectedLog.real_ip && (
                    <Button size="small" type="link" danger icon={<StopOutlined />}
                      onClick={() => quickBlock('ip', selectedLog.real_ip)}>拉黑</Button>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="WebRTC IPs" span={2}>
                {selectedLog.webrtc_ips
                  ? selectedLog.webrtc_ips.split(',').map((ip, i) => (
                      <Tag key={i} color="volcano" style={{ marginBottom: 4, cursor: 'pointer' }}
                        onClick={() => quickBlock('webrtc_ip', ip)}>{ip} ✕</Tag>
                    ))
                  : '-'
                }
              </Descriptions.Item>
              <Descriptions.Item label="ASN">
                <Space>
                  <Tag color="blue">{selectedLog.asn || '-'}</Tag>
                  {selectedLog.asn && (
                    <Button size="small" type="link" danger icon={<StopOutlined />}
                      onClick={() => quickBlock('asn', selectedLog.asn)}>拉黑</Button>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="拦截原因">
                {selectedLog.block_reason || '-'}
              </Descriptions.Item>
            </Descriptions>

            {/* 合规评分 */}
            {selectedLog.risk_score !== null && selectedLog.risk_score !== undefined && (
              <Card size="small" style={{ marginBottom: 16, borderRadius: 8,
                background: parseFloat(selectedLog.risk_score) >= 8 ? '#f6ffed' : parseFloat(selectedLog.risk_score) >= 5 ? '#fffbe6' : parseFloat(selectedLog.risk_score) >= 3 ? '#fff7e6' : '#fff1f0',
                borderColor: parseFloat(selectedLog.risk_score) >= 8 ? '#b7eb8f' : parseFloat(selectedLog.risk_score) >= 5 ? '#ffe58f' : parseFloat(selectedLog.risk_score) >= 3 ? '#ffd591' : '#ffa39e'
              }}>
                <Space>
                  <Text strong>指纹合规评分:</Text>
                  <Tag color={parseFloat(selectedLog.risk_score) >= 8 ? '#52c41a' : parseFloat(selectedLog.risk_score) >= 5 ? '#faad14' : parseFloat(selectedLog.risk_score) >= 3 ? '#ff7a45' : '#ff4d4f'}
                    style={{ fontSize: 16, fontWeight: 700, padding: '2px 12px' }}>
                    {parseFloat(selectedLog.risk_score)}/10
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {parseFloat(selectedLog.risk_score) >= 8 ? '✅ 低风险 - 可能是真实设备' : parseFloat(selectedLog.risk_score) >= 5 ? '⚠️ 中风险 - 部分指标异常' : parseFloat(selectedLog.risk_score) >= 3 ? '🔶 高风险 - 多项指标异常' : '🔴 极高风险 - 极可能是伪造设备'}
                  </Text>
                </Space>
              </Card>
            )}

            {/* 设备指纹 */}
            <Descriptions
              title={<><MobileOutlined /> 设备指纹</>}
              size="small"
              column={2}
              bordered
              style={{ marginBottom: 20 }}
            >
              <Descriptions.Item label="指纹ID" span={2}>
                <Space>
                  <Text code copyable style={{ fontSize: 11 }}>
                    {selectedLog.fingerprint || '-'}
                  </Text>
                  {selectedLog.fingerprint && (
                    <Button size="small" type="link" danger icon={<StopOutlined />}
                      onClick={() => quickBlock('fingerprint', selectedLog.fingerprint)}>拉黑</Button>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="User-Agent" span={2}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Paragraph style={{ fontSize: 11, margin: 0, maxWidth: 400 }} ellipsis={{ rows: 2, expandable: true }}>
                    {selectedLog.user_agent || fpData?.ua || '-'}
                  </Paragraph>
                  {(selectedLog.user_agent || fpData?.ua) && (
                    <Button size="small" type="link" danger icon={<StopOutlined />}
                      onClick={() => quickBlock('user_agent', selectedLog.user_agent || fpData?.ua)}>拉黑此UA</Button>
                  )}
                </Space>
              </Descriptions.Item>
            </Descriptions>

            {/* 详细指纹数据 */}
            {fpData && (
              <Descriptions
                title="原始指纹数据"
                size="small"
                column={2}
                bordered
              >
                <Descriptions.Item label="屏幕">
                  {fpData.sc || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="像素比">
                  {fpData.pr || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="平台">
                  {fpData.pf || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="语言">
                  {fpData.ln || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="时区">
                  {fpData.tz || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="时区偏移">
                  {fpData.to ?? '-'}
                </Descriptions.Item>
                <Descriptions.Item label="CPU核心数">
                  {fpData.hc || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="内存(GB)">
                  {fpData.dm || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="触控点">
                  {fpData.mt || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Do Not Track">
                  {fpData.dt || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Canvas" span={2}>
                  <Space>
                    <Text code style={{ fontSize: 10 }}>{fpData.c || '-'}</Text>
                    {fpData.c && fpData.c !== 'e' && (
                      <Button size="small" type="link" danger icon={<StopOutlined />}
                        onClick={() => quickBlock('canvas', fpData.c)}>拉黑</Button>
                    )}
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="WebGL厂商" span={2}>
                  {fpData.gv || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="WebGL渲染器" span={2}>
                  <Text style={{ fontSize: 11 }}>{fpData.gr || '-'}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="音频指纹">
                  {fpData.au || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Cookie">
                  {fpData.ce ? '是' : '否'}
                </Descriptions.Item>
                <Descriptions.Item label="LocalStorage">
                  {fpData.ls2 ? '是' : '否'}
                </Descriptions.Item>
                <Descriptions.Item label="WebRTC支持">
                  {fpData.rtc ? '是' : '否'}
                </Descriptions.Item>
                <Descriptions.Item label="字体" span={2}>
                  <Paragraph style={{ fontSize: 10, margin: 0 }} ellipsis={{ rows: 2, expandable: true }}>
                    {fpData.ft || '-'}
                  </Paragraph>
                </Descriptions.Item>
                <Descriptions.Item label="插件" span={2}>
                  <Paragraph style={{ fontSize: 10, margin: 0 }} ellipsis={{ rows: 2, expandable: true }}>
                    {fpData.pg || '-'}
                  </Paragraph>
                </Descriptions.Item>
                <Descriptions.Item label="语言列表" span={2}>
                  {fpData.ls || '-'}
                </Descriptions.Item>
              </Descriptions>
            )}

            {!fpData && (
              <Card size="small" style={{ background: '#fafafa', textAlign: 'center' }}>
                <Text type="secondary">无指纹数据（旧记录或直链访问）</Text>
              </Card>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default LogsPage;
